import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { RootState } from '../store';
import {
  useGetAdminsQuery,
  useCreateAdminMutation,
  useUpdateUserRoleMutation,
  useDeleteUserMutation,
} from '../store/adminApi';
import DataTable from './DataTable';
import Pagination from './Pagination';
import ConfirmModal from './ConfirmModal';
import { User, UserRole } from '../types';

const ITEMS_PER_PAGE = 10;

const AdminList: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const { data: admins = [], isLoading, refetch } = useGetAdminsQuery();
  const [createAdmin, { isLoading: creating }] = useCreateAdminMutation();
  const [updateRole] = useUpdateUserRoleMutation();
  const [deleteUser] = useDeleteUserMutation();

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', designation: '' });
  const [formErr, setFormErr] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'delete' | 'demote' | 'promote';
    user: User;
  } | null>(null);

  const paginated = admins.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(admins.length / ITEMS_PER_PAGE);

  const validate = () => {
    const err: Record<string, string> = {};
    if (!form.name.trim()) err.name = 'Name is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) err.email = 'Valid email is required';
    if (!form.password || form.password.length < 6) err.password = 'Password must be at least 6 characters';
    if (!form.designation.trim()) err.designation = 'Designation is required';
    setFormErr(err);
    return Object.keys(err).length === 0;
  };

  const handleCreate = async () => {
    if (!validate()) return;
    try {
      await createAdmin(form).unwrap();
      toast.success(`Admin ${form.name} created successfully`);
      setForm({ name: '', email: '', password: '', designation: '' });
      setShowCreate(false);
      refetch();
    } catch (e: any) {
      toast.error(e?.data?.message || 'Failed to create admin');
    }
  };

  const handleRoleChange = async (user: User, newRole: UserRole) => {
    try {
      await updateRole({ userId: user._id, role: newRole }).unwrap();
      toast.success(`${user.name}'s role updated to ${newRole}`);
      setConfirmOpen(false);
      refetch();
    } catch (e: any) {
      toast.error(e?.data?.message || 'Failed to update role');
    }
  };

  const handleDelete = async (user: User) => {
    try {
      await deleteUser(user._id).unwrap();
      toast.success(`${user.name} deleted`);
      setConfirmOpen(false);
      refetch();
    } catch (e: any) {
      toast.error(e?.data?.message || 'Failed to delete user');
    }
  };

  const roleBadge = (role: UserRole) => {
    if (role === 'superadmin') return <span className="badge-purple">Super Admin</span>;
    if (role === 'admin') return <span className="badge-orange">Admin</span>;
    return <span className="badge-blue">User</span>;
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display font-bold text-xl text-trident-gray-900">Admin Management</h2>
          <p className="text-sm text-trident-gray-500">
            Manage super admins and admins. Total: {admins.length}
          </p>
        </div>
        <button onClick={() => setShowCreate(!showCreate)} className="btn-primary flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Admin
        </button>
      </div>

      {showCreate && (
        <div className="card mb-5 border-2 border-trident-orange/30 animate-slide-up">
          <h3 className="font-display font-bold text-lg text-trident-gray-900 mb-4">Create New Admin</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-display font-semibold text-trident-gray-600 uppercase tracking-wider mb-1.5">Full Name</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className="input-field"
                placeholder="Jane Doe"
              />
              {formErr.name && <p className="text-xs text-red-500 mt-1">{formErr.name}</p>}
            </div>
            <div>
              <label className="block text-xs font-display font-semibold text-trident-gray-600 uppercase tracking-wider mb-1.5">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                className="input-field"
                placeholder="jane@tridentsqa.com"
              />
              {formErr.email && <p className="text-xs text-red-500 mt-1">{formErr.email}</p>}
            </div>
            <div>
              <label className="block text-xs font-display font-semibold text-trident-gray-600 uppercase tracking-wider mb-1.5">Password</label>
              <input
                type="password"
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                className="input-field"
                placeholder="Min 6 characters"
              />
              {formErr.password && <p className="text-xs text-red-500 mt-1">{formErr.password}</p>}
            </div>
            <div>
              <label className="block text-xs font-display font-semibold text-trident-gray-600 uppercase tracking-wider mb-1.5">Designation</label>
              <input
                type="text"
                value={form.designation}
                onChange={e => setForm({ ...form, designation: e.target.value })}
                className="input-field"
                placeholder="Content Manager"
              />
              {formErr.designation && <p className="text-xs text-red-500 mt-1">{formErr.designation}</p>}
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleCreate} disabled={creating} className="btn-primary">
              {creating ? 'Creating...' : 'Create Admin'}
            </button>
            <button onClick={() => { setShowCreate(false); setFormErr({}); }} className="btn-secondary">
              Cancel
            </button>
          </div>
        </div>
      )}

      <DataTable
        loading={isLoading}
        rows={paginated}
        rowKey={(r) => r._id}
        emptyMessage="No admins yet"
        columns={[
          {
            key: 'name', label: 'Name',
            render: (u) => (
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-trident-gradient flex items-center justify-center text-white font-display font-bold text-xs shadow-trident">
                  {u.name.split(' ').filter(Boolean).map(w => w[0].toUpperCase()).slice(0, 2).join('')}
                </div>
                <div>
                  <div className="font-semibold text-trident-gray-900">{u.name}</div>
                  <div className="text-xs text-trident-gray-500">{u.designation}</div>
                </div>
              </div>
            ),
          },
          { key: 'email', label: 'Email' },
          { key: 'role', label: 'Role', render: (u) => roleBadge(u.role) },
          {
            key: 'verified', label: 'Verified', align: 'center',
            render: (u) => u.isVerified
              ? <span className="badge-green">✓ Yes</span>
              : <span className="badge-gray">Pending</span>,
          },
          {
            key: 'actions', label: 'Actions', align: 'right',
            render: (u) => {
              const isSelf = u._id === currentUser?._id;
              if (isSelf) return <span className="text-xs text-trident-gray-400 italic">You</span>;
              return (
                <div className="flex items-center justify-end gap-2">
                  {u.role === 'admin' && (
                    <button
                      onClick={() => { setConfirmAction({ type: 'promote', user: u }); setConfirmOpen(true); }}
                      className="text-xs font-semibold text-purple-600 hover:text-purple-700 hover:underline"
                    >
                      Promote
                    </button>
                  )}
                  {u.role === 'superadmin' && (
                    <button
                      onClick={() => { setConfirmAction({ type: 'demote', user: u }); setConfirmOpen(true); }}
                      className="text-xs font-semibold text-yellow-600 hover:text-yellow-700 hover:underline"
                    >
                      Demote
                    </button>
                  )}
                  <button
                    onClick={() => { setConfirmAction({ type: 'delete', user: u }); setConfirmOpen(true); }}
                    className="text-xs font-semibold text-red-500 hover:text-red-700 hover:underline"
                  >
                    Delete
                  </button>
                </div>
              );
            },
          },
        ]}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={admins.length}
        itemsPerPage={ITEMS_PER_PAGE}
        onPageChange={setCurrentPage}
      />

      <ConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          if (!confirmAction) return;
          if (confirmAction.type === 'delete') handleDelete(confirmAction.user);
          if (confirmAction.type === 'promote') handleRoleChange(confirmAction.user, 'superadmin');
          if (confirmAction.type === 'demote') handleRoleChange(confirmAction.user, 'admin');
        }}
        title={
          confirmAction?.type === 'delete' ? 'Delete user?'
            : confirmAction?.type === 'promote' ? 'Promote to Super Admin?'
              : 'Demote to Admin?'
        }
        message={
          confirmAction?.type === 'delete'
            ? `Permanently delete ${confirmAction?.user.name}? This cannot be undone.`
            : confirmAction?.type === 'promote'
              ? `Grant super admin privileges to ${confirmAction?.user.name}?`
              : `Remove super admin privileges from ${confirmAction?.user.name}?`
        }
        confirmText={confirmAction?.type === 'delete' ? 'Delete' : 'Confirm'}
        confirmButtonColor={confirmAction?.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-trident-gradient hover:bg-trident-gradient-hover'}
      />
    </div>
  );
};

export default AdminList;
